<template>
	<v-dialog id="bulkEdit" v-model="showDialog" max-width="1024px" persistent>
		<v-card>
			<div class="pa-4">
				<div class="text-h5 text-center mb-2">Merge Entities</div>
				<div class="text-subtitle-1 text-center text--secondary">
					Please select your primary record from the grid below
				</div>
			</div>

			<v-card-text>
				<div class="grid-flex-container">
					<div class="generic-grid-container">
						<v-data-table
							:footer-props="{
								itemsPerPageOptions: [
									10, 15, 20, 25, 30, 50, 100,
								],
								showFirstLastPage: true,
								showCurrentPage: true,
							}"
							:headers="computedHeaders"
							:items="items"
							class="elevation-1"
							dense
							@click:row="onRowClick"
						>
							<template #[`header.select`]="{}">
								<span>Select</span>
							</template>

							<template #[`item.select`]="{ item }">
								<div class="d-flex align-center">
									<v-checkbox
										class="ma-0 mr-2"
										hide-details
										:input-value="isRowSelected(item)"
										@change="onRowSelected(item)"
										@click.stop
									></v-checkbox>
									<div
										class="text-caption grey--text"
										style="pointer-events: none"
									>
										<template v-if="loadingDependencies">
											<v-progress-circular
												indeterminate
												size="12"
												width="1"
												class="mr-1"
											></v-progress-circular>
											Loading...
										</template>
										<template v-else>
											{{ getDependencyCount(item) }}
											Dependencies
										</template>
									</div>
								</div>
							</template>

							<!-- Dynamic slots for each column with custom formatting -->
							<template
								v-for="column in visibleColumns"
								v-slot:[`item.${column.FieldName}`]="{ item }"
							>
								<div :key="column.FieldName">
									<template
										v-if="
											column.DataField &&
											column.DataField.DataType ===
												FieldDataTypes.APPROVAL_STATUS
										"
									>
										<span
											v-if="
												item[column.FieldName] ===
												ApprovalStatus.None
											"
											>None</span
										>
										<span
											v-if="
												item[column.FieldName] ===
												ApprovalStatus.Pending
											"
											>Pending</span
										>
										<span
											v-if="
												item[column.FieldName] ===
												ApprovalStatus.Approved
											"
											>Approved</span
										>
									</template>
									<template
										v-else-if="
											column.DataField &&
											column.DataField.DataType ===
												FieldDataTypes.FILE &&
											item[column.FieldName]
										"
									>
										<v-menu
											order="2"
											content-class="grid-toolbar-menu"
											offset-y
										>
											<template
												#activator="{ attrs, on }"
											>
												<v-btn
													fab
													style="
														background-color: transparent;
													"
													x-small
													elevation="0"
													v-bind="attrs"
													v-on="on"
												>
													<v-icon color="primary"
														>mdi-file-download-outline</v-icon
													>
												</v-btn>
											</template>
											<v-list>
												<v-list-item
													single
													@click="
														previewFile(
															item[
																column.FieldName +
																	'_Path'
															]
														)
													"
												>
													<v-list-item-title
														>Open in New
														Tab</v-list-item-title
													>
												</v-list-item>
												<v-list-item
													single
													@click="
														downloadFile(
															item[
																column.FieldName
															]
														)
													"
												>
													<v-list-item-title
														>Download</v-list-item-title
													>
												</v-list-item>
											</v-list>
										</v-menu>
										<span
											style="cursor: pointer"
											@click="
												downloadFile(
													item[column.FieldName]
												)
											"
										>
											{{
												item[
													column.FieldName +
														'_FileName'
												]
											}}
										</span>
									</template>
									<template
										v-else-if="
											column.DataField &&
											(column.DataField.DataType ==
												FieldDataTypes.DATETIME ||
												column.DataField.DataType ==
													FieldDataTypes.AUTOGENERATED_DATETIME)
										"
									>
										{{
											getDateTimeFormat(
												item[column.FieldName]
											)
										}}
									</template>
									<template
										v-else-if="
											column.DataField &&
											column.DataField.DataType ==
												FieldDataTypes.DATE
										"
									>
										{{
											getDateFormat(
												item[column.FieldName]
											)
										}}
									</template>
									<template
										v-else-if="
											column.DataField &&
											column.DataField.DataType ==
												FieldDataTypes.USER
										"
									>
										{{
											getUserDisplay(
												item[column.FieldName]
											)
										}}
									</template>
									<template v-else>
										{{ item[column.FieldName] }}
									</template>
								</div>
							</template>
						</v-data-table>
					</div>
				</div>
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn @click.stop="onClose" :disabled="mergingRecords"
					>Close</v-btn
				>
				<v-btn
					color="primary"
					@click.stop="onSave"
					:disabled="!selectedItem || mergingRecords || !canMerge"
					v-if="canMerge"
				>
					<v-icon class="mr-2">mdi-merge</v-icon>
					Merge Records
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { FieldDataTypes } from '../../../enums/fieldDataTypes';
import { approvalStatus } from '../../../enums/approvalStatus';
import { GeneralSettingsGetters } from '../../../store/getters';
import dataService from '../../../services/data/projectGridsService';
import { mapGetters } from 'vuex';
import projectSettingsMixin from '../../../mixins/projectSettingsMixin';
import permissionsMixin from '../../../mixins/permissionsMixin';
import { GridsActions } from '../../../store/actions';
import { GeneralActions } from '../../../store/actions';
import { Permissions } from '../../../enums/permissionsEnum';
import vueAppSubfolder from '../../../utils/appSubfolderUtils';

export default {
	name: 'MergeEntitiesModal',
	mixins: [projectSettingsMixin, permissionsMixin],
	props: {
		showDialog: Boolean,
		gridView: Object,
		items: Array,
		visibleColumns: Array,
		irisUserMetadata: Array,
	},
	data() {
		return {
			selectedItemId: null, // Use ID instead of object reference
			dependencyCounts: {}, // Store dependency counts to prevent recalculation
			loadingDependencies: false,
			mergingRecords: false, // Track merge operation loading state
		};
	},

	computed: {
		...mapGetters({
			userDisplayFormat: [GeneralSettingsGetters.USER_DISPLAY_FORMAT],
		}),
		canMerge() {
			return this.hasPermissions([Permissions.Grids.Merge]);
		},
		selectedItem() {
			// Computed property to get the selected item object
			if (!this.selectedItemId || !this.items) return null;
			return this.items.find((item) => item.Id === this.selectedItemId);
		},
		tableHeaders() {
			if (!this.visibleColumns) return [];
			return this.visibleColumns.map((column) => ({
				text: column.Label,
				value: column.FieldName,
				sortable: true,
			}));
		},
		computedHeaders() {
			const selectHeader = {
				text: 'Select',
				value: 'select',
				sortable: false,
				width: '150px',
			};

			const columnHeaders = this.visibleColumns
				? this.visibleColumns.map((column) => ({
						text: column.Label,
						value: column.FieldName,
						sortable: true,
				  }))
				: [];

			return [selectHeader, ...columnHeaders];
		},
		FieldDataTypes() {
			return FieldDataTypes;
		},
		ApprovalStatus() {
			return approvalStatus;
		},
	},
	watch: {
		showDialog(newVal) {
			if (newVal) {
				// Reset selection when modal opens
				this.selectedItemId = null;
				// Load dependencies when dialog is opened
				if (this.items && this.items.length > 0) {
					this.loadMergeDependencies();
				}
			}
		},
	},
	mounted() {
		// Load dependencies when component is mounted and dialog is open
		if (this.showDialog && this.items && this.items.length > 0) {
			this.loadMergeDependencies();
		}
	},
	methods: {
		// Load merge dependencies from API
		loadMergeDependencies() {
			if (!this.items || this.items.length === 0 || !this.gridView)
				return;

			this.loadingDependencies = true;

			const itemIds = this.items.map((item) => item.Id);
			dataService
				.getMergeDependencies(this.gridView.EntityId, itemIds)
				.then((response) => {
					// Map the response to dependency counts
					if (response.data && response.data.Items) {
						const newDependencyCounts = {};
						response.data.Items.forEach((item) => {
							// Keep null values as null to indicate unavailable data
							newDependencyCounts[item.Id] =
								item.DependenciesCount;
						});
						this.dependencyCounts = newDependencyCounts;
					}
				})
				.catch((error) => {
					this.$store.dispatch(GeneralActions.ERROR, error);
					// Set dependency counts to null to indicate unavailable data
					this.items.forEach((item) => {
						this.dependencyCounts[item.Id] = null;
					});
				})
				.finally(() => {
					this.loadingDependencies = false;
				});
		},
		// Row click handler for selection
		onRowClick(item) {
			this.onRowSelected(item);
		},
		// Get dependency count for an item
		getDependencyCount(item) {
			// Return the actual dependency count from API response
			const count = this.dependencyCounts[item.Id];
			return count !== null && count !== undefined ? count : '-';
		},
		// Single selection methods for checkbox
		isRowSelected(item) {
			return this.selectedItemId === item.Id;
		},
		onRowSelected(item) {
			// For single selection: if clicking the same item, deselect it, otherwise select the new item
			if (this.selectedItemId === item.Id) {
				this.selectedItemId = null;
			} else {
				this.selectedItemId = item.Id;
			}
		},
		previewFile(item) {
			const indexOfFirst = item.indexOf('Resources');
			let filePath = item.substring(indexOfFirst);
			filePath = filePath.replace(`\\`, '/');

			let urlPath =
				window.location.origin +
				(vueAppSubfolder.get() === '/' ? '' : vueAppSubfolder.get()) +
				'/uploads';
			+filePath;
			window.open(urlPath, '_blank');
		},
		downloadFile(item) {
			this.$store.dispatch(GridsActions.DOWNLOAD_FILE, item);
		},
		getUserDisplay(userId) {
			var user = this.irisUserMetadata.find((x) => x.Id == userId);
			if (user) {
				return user.IrisUserFormat;
			}
			return userId;
		},

		onClose() {
			// Clear selection when closing
			this.selectedItemId = null;
			this.$emit('close');
		},
		onSave() {
			// Validate selection
			if (!this.selectedItem || !this.items || this.items.length === 0) {
				return;
			}

			// Start loading
			this.mergingRecords = true;
			this.$store.dispatch(GeneralActions.LOADING, true);

			// Get all item IDs for the merge request
			const itemIds = this.items.map((item) => item.Id);

			// Call the merge API
			dataService
				.mergeRecords(
					this.gridView.EntityId,
					this.selectedItem.Id,
					itemIds
				)
				.then(() => {
					// Success - emit refresh event to trigger grid reload and close modal
					this.$emit('refresh');

					// Show success message (optional)
					this.$store.dispatch(GeneralActions.CLEAR_ERROR);
				})
				.catch((error) => {
					// Show error modal
					this.$store.dispatch(GeneralActions.ERROR, error);
				})
				.finally(() => {
					// Stop loading
					this.mergingRecords = false;
					this.$store.dispatch(GeneralActions.LOADING, false);
				});
		},
	},
};
</script>

<style>
.v-badge__badge {
	font-size: 10px;
	color: black;
	height: 18px;
	min-width: 18px;
}
</style>
